// English translations
export const en: Record<string, string> = {
  // ==================== COMMON ====================
  'common.loading': 'Loading...',
  'common.error': 'An error occurred',
  'common.retry': 'Retry',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.continue': 'Continue',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.skip': 'Skip',
  'common.done': 'Done',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.ok': 'OK',
  'common.check': 'Check',
  'common.correct': 'Correct!',
  'common.incorrect': 'Incorrect',
  'common.tryAgain': 'Try again',
  'common.start': 'Start',
  'common.finish': 'Finish',
  'common.submit': 'Submit',

  // ==================== NAVIGATION ====================
  'nav.home': 'Home',
  'nav.learn': 'Learn',
  'nav.courses': 'Courses',
  'nav.vocabulary': 'Vocabulary',
  'nav.profile': 'Profile',
  'nav.settings': 'Settings',
  'nav.logout': 'Log out',
  'nav.leaderboard': 'Leaderboard',
  'nav.aiTutor': 'AI Tutor',
  'nav.culture': 'Culture',
  'nav.chat': 'Chat',
  'nav.ranking': 'Ranking',
  'nav.family': 'Family',
  'nav.premium': 'Premium',

  // ==================== HOME PAGE ====================
  'home.greeting': 'Hello',
  'home.streak': 'day streak',
  'home.dailyGoal': 'Daily Goal',
  'home.myLanguages': 'My Languages',
  'home.addLanguage': 'Add Language',
  'home.recommended': 'Recommended',
  'home.continue': 'Continue Learning',
  'home.selectLanguage': 'Select a language',
  'home.totalXp': 'Total XP',
  'home.bestStreak': 'Best Streak',
  'home.level': 'Level',

  // ==================== CHAT ====================
  'chat.aiTutor': 'AI Tutor',
  'chat.aiTutorDesc': 'Practice conversation with AI',

  // ==================== ONBOARDING ====================
  'onboarding.welcome': 'Welcome to SpeakFlow!',
  'onboarding.welcome.subtitle': 'Learn any language with AI-powered conversations',
  'onboarding.selectLanguage': 'Select your language',
  'onboarding.selectLanguageDesc': 'Choose the language for the app interface',
  'onboarding.selectUILanguage': 'Choose your language',
  'onboarding.selectUILanguage.subtitle': 'This will be used for the app interface',
  'onboarding.selectLearningLanguage': 'What would you like to learn?',
  'onboarding.selectLearningLanguage.subtitle': 'You can learn multiple languages',
  'onboarding.selectDailyGoal': 'Set your daily goal',
  'onboarding.selectDailyGoal.subtitle': 'How much time can you spend each day?',
  'onboarding.selectGoal': 'What is your learning goal?',
  'onboarding.selectGoalDesc': 'We\'ll personalize your learning path',
  'onboarding.takeTest': 'Take Level Test',
  'onboarding.takeTestDesc': 'Find out your level in 5 minutes',
  'onboarding.skipTest': 'Skip for now',
  'onboarding.skipTestDesc': 'Start from the beginning',
  'onboarding.letsGo': 'Let\'s Go!',
  'onboarding.step': 'Step',
  'onboarding.of': 'of',
  'onboarding.ready': 'You\'re all set!',
  'onboarding.ready.subtitle': 'Let\'s start your language learning journey',
  'onboarding.features.languages': '4 Languages',
  'onboarding.features.aiTutor': 'AI Tutor',
  'onboarding.features.dailyGoals': 'Daily Goals',

  // Daily Goal options
  'dailyGoal.5min': '5 minutes',
  'dailyGoal.10min': '10 minutes',
  'dailyGoal.15min': '15 minutes',
  'dailyGoal.20min': '20 minutes',
  'dailyGoal.casual': 'Casual pace',
  'dailyGoal.regular': 'Regular practice',
  'dailyGoal.serious': 'Serious learner',
  'dailyGoal.intense': 'Intensive training',

  // ==================== LEVEL TEST ====================
  'levelTest.title': 'Level Test',
  'levelTest.subtitle': 'Let\'s find your Korean level',
  'levelTest.duration': 'About 5 minutes',
  'levelTest.questions': 'questions',
  'levelTest.vocabulary': 'Vocabulary',
  'levelTest.grammar': 'Grammar',
  'levelTest.listening': 'Listening',
  'levelTest.speaking': 'Speaking',
  'levelTest.analyzing': 'Analyzing your results...',
  'levelTest.yourLevel': 'Your Level',
  'levelTest.congratulations': 'Congratulations!',
  'levelTest.resultDesc': 'Based on your test results',
  'levelTest.startLearning': 'Start Learning',
  'levelTest.retakeTest': 'Retake Test',
  'levelTest.sectionComplete': 'Section Complete',
  'levelTest.nextSection': 'Next Section',
  'levelTest.listenAndAnswer': 'Listen and answer',
  'levelTest.speakNow': 'Speak now',
  'levelTest.tapToListen': 'Tap to listen',

  // ==================== CEFR LEVELS ====================
  'cefr.A1': 'Beginner',
  'cefr.A2': 'Elementary',
  'cefr.B1': 'Intermediate',
  'cefr.B2': 'Upper Intermediate',
  'cefr.C1': 'Advanced',
  'cefr.C2': 'Mastery',
  'cefr.A1.desc': 'Basic greetings and simple expressions',
  'cefr.A2.desc': 'Simple conversations about daily life',
  'cefr.B1.desc': 'Can handle most travel situations',
  'cefr.B2.desc': 'Can interact fluently with native speakers',
  'cefr.C1.desc': 'Can express ideas fluently and spontaneously',
  'cefr.C2.desc': 'Near-native proficiency in all situations',

  // ==================== LEAGUE / RANKING ====================
  'league.title': 'League',
  'league.weeklyRanking': 'Weekly Ranking',
  'league.bronze': 'Bronze',
  'league.silver': 'Silver',
  'league.gold': 'Gold',
  'league.platinum': 'Platinum',
  'league.diamond': 'Diamond',
  'league.promotion': 'Promotion Zone',
  'league.demotion': 'Demotion Zone',
  'league.safe': 'Safe Zone',
  'league.weeklyXP': 'Weekly XP',
  'league.rank': 'Rank',
  'league.daysLeft': 'days left',
  'league.promoted': 'Promoted!',
  'league.demoted': 'Demoted',
  'league.stayed': 'Stayed',
  'league.noData': 'No ranking data yet',
  'league.earnXP': 'Earn XP to join the league!',
  'league.topPercent': 'Top {percent}%',

  // ==================== LANDING PAGE ====================
  'landing.tagline': '#1 Korean Learning App',
  'landing.title': 'Learn Korean',
  'landing.titleAccent': 'The Fun Way',
  'landing.subtitle': 'Master Korean with bite-sized lessons, traditional culture content, and AI-powered pronunciation feedback. Join 1M+ learners!',
  'landing.getStarted': 'Get Started Free',
  'landing.learnMore': 'Learn More',
  'landing.login': 'Log In',
  'landing.startFree': 'Start Free',
  'landing.learners': 'Learners',
  'landing.lessons': 'Lessons',
  'landing.countries': 'Countries',

  // ==================== FEATURES ====================
  'features.title': 'Why KoreanFlow?',
  'features.subtitle': 'Our unique approach combines gamification, authentic content, and AI technology to make learning Korean fun and effective.',
  'features.streaks.title': 'Daily Streaks',
  'features.streaks.desc': 'Build a habit with daily goals and streak rewards. Stay motivated with XP and gems.',
  'features.folksong.title': 'Folk Songs',
  'features.folksong.desc': 'Learn Korean through traditional folk songs. Understand lyrics and culture!',
  'features.culture.title': 'Korean Culture',
  'features.culture.desc': 'Explore proverbs, slang, and essential phrases. Master authentic expressions.',
  'features.ai.title': 'AI Chatbot',
  'features.ai.desc': 'Practice conversations with our AI tutor. Get instant feedback and corrections.',
  'features.leaderboard.title': 'Leaderboards',
  'features.leaderboard.desc': 'Compete with friends and learners worldwide. Climb the weekly rankings!',
  'features.srs.title': 'SRS Vocabulary',
  'features.srs.desc': 'Smart repetition system ensures you remember what you learn. Forever.',
  'features.pronunciation.title': 'Pronunciation Feedback',
  'features.pronunciation.desc': 'AI-powered pronunciation analysis with detailed scoring.',

  // ==================== CTA ====================
  'cta.title': 'Ready to Start Your Journey?',
  'cta.subtitle': 'Join millions of learners and start speaking Korean today. It\'s free!',
  'cta.button': 'Start Learning Free',

  // ==================== AUTH ====================
  'auth.login': 'Log In',
  'auth.register': 'Sign Up',
  'auth.email': 'Email',
  'auth.emailPlaceholder': 'Enter your email',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': 'Create a password',
  'auth.confirmPassword': 'Confirm Password',
  'auth.forgotPassword': 'Forgot password?',
  'auth.noAccount': 'Don\'t have an account?',
  'auth.haveAccount': 'Already have an account?',
  'auth.orContinueWith': 'or continue with',
  'auth.google': 'Google',
  'auth.apple': 'Apple',
  'auth.createAccount': 'Create Account',
  'auth.createAccountTitle': 'Create Your Account',
  'auth.createAccountSubtitle': 'Start your Korean learning journey today',
  'auth.username': 'Username',
  'auth.usernamePlaceholder': 'Choose a username',
  'auth.usernameHint': 'Letters, numbers, and underscores only',
  'auth.passwordHint': 'At least 8 characters with uppercase, lowercase, and number',
  'auth.nativeLanguage': 'Native Language',
  'auth.nativeLanguageHint': 'We\'ll show explanations in this language',
  'auth.termsAgree': 'By signing up, you agree to our',
  'auth.terms': 'Terms',
  'auth.and': 'and',
  'auth.privacyPolicy': 'Privacy Policy',
  'auth.loginTitle': 'Welcome Back',
  'auth.loginSubtitle': 'Sign in to continue learning',
  'auth.loginButton': 'Log In',
  'auth.registerButton': 'Sign Up',
  'auth.rememberMe': 'Remember me',

  // ==================== DASHBOARD ====================
  'dashboard.welcome': 'Welcome back',
  'dashboard.todayGoal': 'Today\'s Goal',
  'dashboard.streak': 'Streak',
  'dashboard.days': 'days',
  'dashboard.xp': 'XP',
  'dashboard.level': 'Level',
  'dashboard.continueLearning': 'Continue Learning',
  'dashboard.recommended': 'Recommended for you',
  'dashboard.myCourses': 'My Courses',
  'dashboard.seeAll': 'See All',
  'dashboard.dailyGoalComplete': 'Daily goal complete!',
  'dashboard.keepGoing': 'Keep going!',
  'dashboard.yourLevel': 'Your Level',
  'dashboard.weeklyProgress': 'Weekly Progress',

  // ==================== COURSES ====================
  'courses.title': 'All Courses',
  'courses.search': 'Search courses...',
  'courses.all': 'All',
  'courses.beginner': 'Beginner',
  'courses.elementary': 'Elementary',
  'courses.intermediate': 'Intermediate',
  'courses.advanced': 'Advanced',
  'courses.lessons': 'lessons',
  'courses.enrolled': 'enrolled',
  'courses.progress': 'Progress',
  'courses.completed': 'completed',
  'courses.recommended': 'Recommended',
  'courses.forYourLevel': 'For your level',
  'courses.locked': 'Locked',
  'courses.unlockWith': 'Unlock with Premium',

  // ==================== LESSON ====================
  'lesson.loading': 'Loading lesson...',
  'lesson.notFound': 'Lesson not found',
  'lesson.quit': 'Are you sure you want to quit? Your progress will be lost.',
  'lesson.complete': 'Lesson Complete!',
  'lesson.perfect': 'Perfect!',
  'lesson.amazing': 'Amazing! You got everything right!',
  'lesson.xpEarned': 'XP Earned',
  'lesson.accuracy': 'Accuracy',
  'lesson.correct': 'Correct',
  'lesson.goBack': 'Go Back',
  'lesson.skip': 'Skip',
  'lesson.unknownType': 'Unknown exercise type',

  // ==================== EXERCISES ====================
  'exercise.tapToReveal': 'Tap to reveal answer',
  'exercise.matchPairs': 'Match the pairs',
  'exercise.matched': 'matched',
  'exercise.arrangeWords': 'Arrange the words',
  'exercise.speak': 'Speak',
  'exercise.listening': 'Listening... Speak now!',
  'exercise.processing': 'Processing your speech...',
  'exercise.tapToSpeak': 'Tap to speak',
  'exercise.cantSpeak': 'Can\'t speak right now? Skip',
  'exercise.again': 'Again',
  'exercise.gotIt': 'Got it!',

  // ==================== PRONUNCIATION ====================
  'pronunciation.title': 'Pronunciation Practice',
  'pronunciation.subtitle': 'Improve your Korean pronunciation with AI feedback',
  'pronunciation.selectLevel': 'Select Your Level',
  'pronunciation.beginner': 'Beginner',
  'pronunciation.intermediate': 'Intermediate',
  'pronunciation.advanced': 'Advanced',
  'pronunciation.score': 'Your Score',
  'pronunciation.accuracy': 'Accuracy',
  'pronunciation.fluency': 'Fluency',
  'pronunciation.intonation': 'Intonation',
  'pronunciation.feedback': 'Feedback',
  'pronunciation.tryAgain': 'Try Again',
  'pronunciation.next': 'Next',
  'pronunciation.listen': 'Listen',
  'pronunciation.slow': 'Slow',
  'pronunciation.sessionComplete': 'Session Complete!',
  'pronunciation.bestScore': 'Best Score',
  'pronunciation.average': 'Average',
  'pronunciation.attempts': 'Attempts',
  'pronunciation.practiceAgain': 'Practice Again',
  'pronunciation.changeLevel': 'Change Level',
  'pronunciation.tips': 'Tips for Better Pronunciation',
  'pronunciation.tip1': 'Listen to the audio first',
  'pronunciation.tip2': 'Speak clearly and at a steady pace',
  'pronunciation.tip3': 'Use the slow playback for difficult words',
  'pronunciation.tip4': 'Practice in a quiet environment',

  // ==================== VOCABULARY ====================
  'vocab.title': 'Vocabulary',
  'vocab.totalWords': 'Total Words',
  'vocab.mastered': 'Mastered',
  'vocab.dueToday': 'Due Today',
  'vocab.reviewSession': 'Review Session',
  'vocab.wordsDue': 'words due for review',
  'vocab.startReview': 'Start Review',
  'vocab.allCaughtUp': 'All caught up!',
  'vocab.noWordsDue': 'No words due for review',
  'vocab.sessionComplete': 'Session Complete!',
  'vocab.allWords': 'All Words',

  // ==================== LEARN PAGE ====================
  'learn.title': 'Learn',
  'learn.currentLevel': 'Current Level',
  'learn.noCoursesYet': 'Coming Soon!',
  'learn.noCoursesDescription': 'Courses for this language are being prepared.',
  'learn.lessonsCompleted': 'lessons completed',
  'learn.startCourse': 'Start Course',
  'learn.continueCourse': 'Continue',
  'learn.courseCompleted': 'Completed',
  'learn.locked': 'Locked',
  'learn.unlockBy': 'Complete previous course to unlock',

  // ==================== COURSE PAGE ====================
  'course.progress': 'Progress',
  'course.lessons': 'Lessons',
  'course.lessonsCompleted': 'lessons completed',
  'course.noLessons': 'Lessons are being prepared...',

  // ==================== LESSON PAGE ====================
  'lesson.tapToReveal': 'Tap to reveal meaning',
  'lesson.examples': 'Examples',
  'lesson.questionsCorrect': 'correct',
  'lesson.wordsLearned': 'Words',
  'lesson.grammarPoints': 'Grammar',
  'lesson.backToCourse': 'Back to Course',
  'lesson.practiceAgain': 'Practice Again',
  'lesson.whatYouLearn': "What you'll learn",
  'lesson.newWords': 'new words',
  'lesson.exercises': 'exercises',
  'lesson.startLesson': 'Start Lesson',
  'lesson.reviewLesson': 'Review Lesson',
  'lesson.learnVocabulary': 'Learn Vocabulary',
  'lesson.grammarPoint': 'Grammar Point',
  'lesson.practice': 'Practice',

  // ==================== PROFILE ====================
  'profile.title': 'Profile',
  'profile.level': 'Level',
  'profile.xpToNext': 'XP to next level',
  'profile.totalXp': 'Total XP',
  'profile.currentStreak': 'Current Streak',
  'profile.lessonsDone': 'Lessons Done',
  'profile.timeLearned': 'Time Learned',
  'profile.achievements': 'Achievements',
  'profile.settings': 'Settings',
  'profile.dailyGoals': 'Daily Goals',
  'profile.allAchievements': 'All Achievements',
  'profile.logOut': 'Log Out',
  'profile.koreanLevel': 'Korean Level',
  'profile.guest': 'Guest',
  'profile.days': 'days',
  'profile.longestStreak': 'Longest',
  'profile.lessonsCompleted': 'completed',
  'profile.dailyGoal': 'Daily Goal',
  'profile.learningLanguages': 'Learning Languages',
  'profile.noLanguages': 'No languages selected',
  'profile.minutesPerDay': 'min/day',
  'profile.learningHistory': 'Learning History',
  'profile.viewProgress': 'View your progress',
  'profile.appSettings': 'App Settings',
  'profile.languageNotifications': 'Language, notifications',
  'profile.loginToSave': 'Log in to save your progress',

  // ==================== SETTINGS ====================
  'settings.title': 'Settings',
  'settings.language': 'App Language',
  'settings.preferences': 'Preferences',
  'settings.sound': 'Sound Effects',
  'settings.soundEffects': 'Sound Effects',
  'settings.soundEffectsDesc': 'Play sounds when answering exercises',
  'settings.notifications': 'Notifications',
  'settings.notificationsDesc': 'Receive daily learning reminders',
  'settings.darkMode': 'Dark Mode',
  'settings.darkModeDesc': 'Dark theme for more comfortable viewing',
  'settings.dailyReminder': 'Daily Reminder',
  'settings.account': 'Account',
  'settings.editProfile': 'Edit Profile',
  'settings.terms': 'Terms of Service',
  'settings.privacy': 'Privacy Policy',
  'settings.help': 'Help',
  'settings.about': 'About',
  'settings.retakeTest': 'Retake Level Test',
  'settings.retakeTestDesc': 'Check your current Korean level',
  'settings.learning': 'Learning',
  'settings.dailyGoal': 'Daily Goal',
  'settings.minutesPerDay': 'minutes per day',
  'settings.selectLanguage': 'Select Language',
  'settings.selectDailyGoal': 'Set Daily Goal',
  'settings.perDay': 'per day',
  'settings.appInfo': 'App Info',
  'settings.logout': 'Log Out',

  // ==================== FOOTER ====================
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'footer.contact': 'Contact',
  'footer.rights': 'All rights reserved.',

  // ==================== COOKIE CONSENT ====================
  'cookie.title': 'We use cookies',
  'cookie.description': 'We use cookies to improve your experience, analyze site traffic, and personalize content. You can choose to accept or reject non-essential cookies.',
  'cookie.learnMore': 'Learn more',
  'cookie.acceptAll': 'Accept All',
  'cookie.rejectAll': 'Reject All',

  // ==================== QUICK ACTIONS ====================
  'quickAction.vocabulary': 'Vocabulary',
  'quickAction.reviewWords': 'Review words',
  'quickAction.allCourses': 'All Courses',
  'quickAction.browseLessons': 'Browse lessons',

  // ==================== COURSE DETAIL ====================
  'courseDetail.notFound': 'Course not found',
  'courseDetail.lessons': 'lessons',
  'courseDetail.completed': 'completed',

  // ==================== BOTTOM NAVIGATION ====================
  'bottomNav.home': 'Home',
  'bottomNav.courses': 'Courses',
  'bottomNav.vocab': 'Vocab',
  'bottomNav.profile': 'Profile',

  // ==================== AI TUTOR ====================
  'aiTutor.title': 'AI Tutor',
  'aiTutor.selectScenario': 'Select a scenario',
  'aiTutor.freeTalk': 'Free Talk',
  'aiTutor.cafe': 'Cafe Order',
  'aiTutor.shopping': 'Shopping',
  'aiTutor.restaurant': 'Restaurant',
  'aiTutor.friends': 'Making Friends',
  'aiTutor.interview': 'Job Interview',
  'aiTutor.typeMessage': 'Type a message...',
  'aiTutor.send': 'Send',

  // ==================== ERRORS ====================
  'error.generic': 'Something went wrong',
  'error.network': 'Network error. Please check your connection.',
  'error.notFound': 'Page not found',
  'error.unauthorized': 'Please log in to continue',
  'error.microphoneAccess': 'Microphone access denied',
  'error.speechNotSupported': 'Speech recognition not supported. Try Chrome browser.',
};

export default en;
